import Box from 'components/ui/box';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { RiArrowLeftSLine } from 'react-icons/ri';
import { Link } from 'react-router-dom';

import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import FeedbackImg from 'assets/img/comportamento/bg-feedback-moedas.png';

import './style.scss';
import { useAuth } from 'context/AuthContext';
import Button from 'components/ui/button';
import { actionLog } from 'services/actionlog.service';
import { IoIosClose } from 'react-icons/io';
import ReactModal from 'react-modal';
import { getRegulation, IRegulation } from 'services/regulation.service';
import { NotFound } from 'pages/NotFound';
import { canRenounceDynamics } from './communication-preferences';

export function Preference() {
  const [load, setLoad] = useState<boolean>(true);
  const [actions, setActions] = useState(true);
  const [task, setTask] = useState(true);
  const [decline, setDecline] = useState(false);
  const [recognition, setRecognition] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [regulation, setRegulation] = useState<IRegulation>();

  const { user, realCycle, refreshUser, userInfo } = useAuth();

  const acceptRef = useRef<any>();
  const modalContentRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const modalTitleId = useId();
  const recognitionId = useId();
  const taskId = useId();
  const actionsId = useId();
  const declineId = useId();

  const isModalOpen = submitted && !load;

  // Foca o botão de fechar ao abrir o modal
  useEffect(() => {
    if (isModalOpen && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [isModalOpen]);

  // Foco trap para modal: Tab/Shift+Tab cíclico entre botão de fechar e conteúdo
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setSubmitted(false);
    }
    if (e.key === 'Tab') {
      const focusable = [closeButtonRef.current, modalContentRef.current].filter(Boolean);
      if (focusable.length < 2) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        if (first) first.focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        if (last) last.focus();
      }
    }
  }, []);

  useEffect(() => {
    if (isModalOpen && modalContentRef.current) {
      modalContentRef.current.focus();
    }
  }, [isModalOpen]);

  useEffect(() => {
    async function loadData() {
      setLoad(true);

      const data = await getRegulation(user?.extra?.rede);
      if (data) {
        setRegulation(data);
      }

      setTask(user?.extra?.task === false ? false : true);
      setActions(user?.extra?.actions === false ? false : true);
      setRecognition(user?.extra?.recognition === false ? false : true);

      setLoad(false);
    }

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  function verifyPreference() {
    if ((!task || !recognition || !actions) && !decline) return true;

    return false;
  }

  const currentCycle = useMemo(() => {
    if (!realCycle) return undefined;
    const cycle = realCycle.find((item: any) => item.status === 'open');

    return !!cycle ? cycle : realCycle[0];
  }, [realCycle]);

  async function handleSubmit() {
    setLoad(true);
    setSubmitted(true);

    await actionLog('me', 'accept_rules', {
      task,
      actions,
      recognition,
    });

    refreshUser();
  }

  function getText(): string {
    const parts = [];
    if (!recognition) parts.push('Reconhecimento');
    if (!task) parts.push('Realizações');
    if (!actions) parts.push('Ações de Engajamento (Ações Ecoa, Desafio Semestral e Bônus de colaboração)');


    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0];
    if (parts.length === 2) return parts.join(' e ');

    return `${parts.slice(0, -1).join(', ')} e ${parts[parts.length - 1]}`;
  }

  function feedback() {
    return (
      <ReactModal
        shouldCloseOnOverlayClick={false}
        isOpen={isModalOpen}
        onRequestClose={() => setSubmitted(false)}
        contentLabel="Preferências atualizadas"
        aria={{
          labelledby: modalTitleId,
          describedby: `${modalTitleId}-desc`,
        }}
        portalClassName="relative z-50"
        overlayClassName="fixed z-100 top-0 left-0 bg-black bg-opacity-60 flex flex-col items-center w-screen h-screen p-6 pt-[10vh] overflow-y-auto"
        className="outline-none"
        shouldReturnFocusAfterClose={true}
      >
        <div
          onKeyDown={handleKeyDown}
          className="w-[90%] lg:w-auto lg:max-w-[600px]"
        >
          <Box className="relative">
            <button
              ref={closeButtonRef}
              type="button"
              className="absolute -right-5 -top-5 rounded-full bg-purple w-10 h-10 p-0 flex justify-center items-center hover:ring hover:ring-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow"
              onClick={() => setSubmitted(false)}
              aria-label="Fechar modal de confirmação"
            >
              <IoIosClose color="white" size={40} aria-hidden="true" />
            </button>

            <div
              ref={modalContentRef}
              className="flex flex-col items-center justify-center"
              tabIndex={-1}
            >
              <div className="flex items-center justify-center mb-6">
                <img src={FeedbackImg} alt="" role="presentation" className="w-[173px] block" />
              </div>

              <h2 id={modalTitleId} className="block text-2xl font-semibold text-yellow mb-4 text-center">
                Preferências Atualizadas!
              </h2>

              <p id={`${modalTitleId}-desc`} className="font-thin">
                Suas preferências foram atualizadas com sucesso!
              </p>
            </div>
          </Box>
        </div>
      </ReactModal>
    );
  }

  /*
    Quem não renuncia a dinâmica não tem o que fazer aqui: a página é a renúncia
    (AD-018). Guard restaurado ao que existia antes desta feature — `ed560909` o
    removera para abrir a página ao opt-out de canal, que saiu em 2026-08-08.

    A condição chama `canRenounceDynamics` em vez de repetir os três campos, que
    é como estava antes. Mesma expressão, com teste próprio, e uma cópia a menos
    para sair de acordo com a outra (AD-017).
  */
  if (!canRenounceDynamics({ isManager: !!userInfo?.is_manager, cadMissao: userInfo?.cad_missao, tipoRo: userInfo?.tipo_ro })) {
    return <NotFound />;
  }

  return (
    <main className="mx-auto container p-3 lg:p-10" aria-busy={load}>
      <nav className="flex flex-ro items-center space-x-3 lg:space-x-6 mb-3 lg:mb-10" aria-label="Navegação da página">
        <Link
          to="../"
          className="rounded-full bg-purple flex justify-center items-center p-1"
          aria-label="Voltar para página anterior"
        >
          <RiArrowLeftSLine size={28} className="text-white" aria-hidden="true" />
        </Link>
        {/* Heading principal da página */}
        <h1 className="text-xl lg:text-3xl" tabIndex={0}>Minhas Preferências</h1>
      </nav>

      <Box>
        {load && (
          <div role="status" aria-live="polite" aria-busy="true" className="flex items-center justify-center">
            <span className="sr-only">Carregando preferências</span>
            <span aria-hidden="true">Carregando...</span>
          </div>
        )}
        {!load && (
          <form
            onSubmit={e => {
              e.preventDefault();
              handleSubmit();
            }}
            aria-label="Formulário de preferências"
          >
            <div className="mb-3 w-full">
              <h2 className="text-center text-yellow text-2xl mb-4" id="config-pref-title" tabIndex={0}>Configurar Preferências</h2>

              <p className="text-center mb-4" tabIndex={0}>
                O Joga Junto possui 3 dinâmicas que você participa. Caso você não queira participar de alguma delas, desmarque a
                dinâmica e confirme o termo de recusa. Você pode modificar essas preferências a qualquer momento até{' '}
                {currentCycle?.date_end && (
                  <time dateTime={new Date(currentCycle.date_end).toISOString()}>
                    {format(new Date(currentCycle.date_end), 'dd/MM/yyyy', { locale: ptBR })}
                  </time>
                )}
              </p>

              <fieldset>
                <legend className="sr-only">Selecione as dinâmicas que deseja participar</legend>

                <div className="p-3 border-b border-gray-200">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id={recognitionId}
                      name="acceptRecognition"
                      className="mr-2"
                      checked={recognition}
                      onChange={e => setRecognition(e.target.checked)}
                      ref={ref => {
                        acceptRef.current = ref;
                      }}
                    />
                    <label htmlFor={recognitionId} className="cursor-pointer">
                      Dinâmica de Reconhecimento
                    </label>
                  </div>
                </div>

                <div className="p-3 border-b border-gray-200">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id={taskId}
                      name="acceptTask"
                      className="mr-2"
                      checked={task}
                      onChange={e => setTask(e.target.checked)}
                    />
                    <label htmlFor={taskId} className="cursor-pointer">
                      Dinâmica de Realizações
                    </label>
                  </div>
                </div>

                <div className="p-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id={actionsId}
                      name="acceptActions"
                      className="mr-2"
                      checked={actions}
                      onChange={e => setActions(e.target.checked)}
                    />
                    <label htmlFor={actionsId} className="cursor-pointer">
                      Dinâmica de Ações de Engajamento (Ações Ecoa, Desafio Semestral e Bônus de colaboração)
                    </label>
                  </div>
                </div>
              </fieldset>

              <div className="mt-2 p-3">
                {(!recognition || !actions || !task) && (
                  <div className="mt-2 space-y-2" role="alert" aria-live="polite">
                    <p className="mr-2 text-sm text-yellow" tabIndex={0}>
                      Conforme Solicitação de Exclusão do Programa Joga Junto BB, os pontos obtidos nessa dinâmica não serão
                      convertidos em premiação.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id={declineId}
                          name="acceptRecognitionDecline"
                          className="mr-2"
                          checked={decline}
                          onChange={e => setDecline(e.target.checked)}
                          aria-describedby="decline-terms"
                        />
                        <label htmlFor={declineId} className="cursor-pointer">
                          Confirmo que estou ciente do termo de renúncia abaixo.
                        </label>
                      </div>

                      <div id="decline-terms" className="space-y-2">
                        <strong className="block" tabIndex={0}>SOLICITAÇÃO DE EXCLUSÃO DO PROGRAMA JOGA JUNTO BB</strong>
                        <p tabIndex={0}>
                          Solicito, para os devidos fins, minha exclusão do Programa Joga Junto BB referente ao Ciclo{' '}
                          {currentCycle?.title}.
                        </p>
                        <p tabIndex={0}>
                          Estou ciente de que minha solicitação de exclusão na(s) Dinâmica(s) ({`${getText()}`}), é irrevogável e
                          irretratável e implica na perda do meu direito a participar das premiações oferecidas no Programa Joga
                          Junto BB durante o semestre acima citado.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-row justify-center space-x-4 mt-4">
              <Button
                type="submit"
                color="clear"
                className="leading-none disabled:opacity-50 p-2 bg-yellow text-blue-dark"
                disabled={verifyPreference()}
                aria-disabled={verifyPreference()}
                aria-describedby={verifyPreference() ? 'button-hint' : undefined}
              >
                Salvar Preferências
              </Button>
              {verifyPreference() && (
                <span id="button-hint" className="sr-only" role="alert">
                  Para salvar, confirme o termo de renúncia acima
                </span>
              )}
            </div>
          </form>
        )}
      </Box>

      {feedback()}
    </main>
  );
}
